import Vue from "vue";
import $ from "jquery";
import AjaxUtils from "../../util/ajax_utils";
import {ApckMarkupToHtml} from "../../vendor/markup/markup.js";
import {ApckWorkflowForm, ApckWorkflowNotesTables} from "./workflow-form";
import {FormatImpactRisk} from "../../util/helpers";
import {ApckScanTable} from "../scans/scan_table";
import DOMPurify from "dompurify";
import { ApckVulnAttachments } from "./vuln-attachments";


const ApckVulnWorkflowAssignee = {
    data: function () {
        return {
            assignee: null,
            assignee_options: [],
            notes: '',
            loading: false,
            errorsKey: 0,
        };
    },
    props: {
        url: String,
        initial_assignee: String,
        errors: {
            type: Object,
            default: () => ({}),
        }
    },
    computed: {
        _assignee_options () {
            return [
                {'label': '--- unassigned ---', 'value': null},
                ...this.assignee_options,
            ];
        }
    },
    mounted: function () {
        const cmp = this;
        cmp.loading = true;
        this.assignee = this.initial_assignee;

        AjaxUtils.ajax_get({
            url: cmp.url,
            success: function (data) {
                if (data) {
                    cmp.$data.assignee_options = data.assignee_options;
                }
                cmp.loading = false;
            },
            fail: function () {
                cmp.loading = false;
            },
            opts: {err_404_message: 'Vulnerability not found'}
        })
    },
    methods: {
        save: function () {
            var cmp = this;
            cmp.loaded = false;

            cmp.$emit('updateErrors', {});
            cmp.errorsKey += 1;

            const data = {
                'assignee': cmp.assignee,
                'notes': cmp.notes,
            };
            AjaxUtils.ajax_post({
                url: this.url,
                data: JSON.stringify(data),
                success: function (json, status, xhr) {
                    cmp.loaded = true;
                    if (cmp.assignee) {
                        const assignee_summary = {
                            '_id': cmp.assignee,
                            'name': cmp.assignee_options.find(item => item.value === cmp.assignee).label
                        };
                        cmp.$emit('assigneeUpdated', assignee_summary);
                    } else {
                        cmp.$emit('assigneeUpdated', null);
                    }
                    cmp.$emit('close');
                },
                fail: function (json, status, xhr) {
                    cmp.$emit('updateErrors', json?.field_errors ? json.field_errors : {});
                    cmp.errorsKey += 1;
                    cmp.loaded = true;
                }
            });
        },
    },
    template: `
        <div ref="modal" :key="errorsKey">
            <apck-field :errors="errors?.assignee">
                <apck-select
                    v-model="assignee"
                    label="Assignee"
                    :options="_assignee_options"
                />
            </apck-field>

            <apck-field :errors="errors?.notes">
                <apck-text
                    v-model="notes"
                    label="Notes"
                    :rows="10"
                />
            </apck-field>

            <apck-button title="Save" value="Save" icon="fa-save" class="btn-positive" :handler="save"/>
            <apck-mask :trigger="loading" :target="(mask) => mask.$parent.$parent.$refs.lightboxouter"/>
        </div>
    `
};

const ApckVulnWorkflowPriority = {
    data: function () {
        return {
            priority: null,
            priority_options: [
                {'label': '-----', 'value': null},
                {'label': 'High', 'value': '4.0-high'},
                {'label': 'Medium', 'value': '3.0-medium'},
                {'label': 'Low', 'value': '2.0-low'},
            ],
            notes: '',
            loading: false,
            errorsKey: 0,
        };
    },
    props: {
        url: String,
        initial_priority: String,
        errors: {
            type: Object,
            default: () => ({}),
        }
    },
    mounted: function () {
        this.priority = this.initial_priority;
    },
    methods: {
        save: function () {
            var cmp = this;
            cmp.loaded = false;

            cmp.$emit('updateErrors', {});
            cmp.errorsKey += 1;

            const data = {
                'priority': cmp.priority,
                'notes': cmp.notes,
            };
            AjaxUtils.ajax_post({
                url: this.url,
                data: JSON.stringify(data),
                success: function (json, status, xhr) {
                    cmp.loaded = true;
                    cmp.$emit('priorityUpdated', cmp.priority);
                    cmp.$emit('close');
                },
                fail: function (json, status, xhr) {
                    cmp.$emit('updateErrors', json?.field_errors ? json.field_errors : {});
                    cmp.errorsKey += 1;
                    cmp.loaded = true;
                }
            });
        },
    },
    template: `
        <div ref="modal" :key="errorsKey">
            <apck-field :errors="errors?.priority">
                <apck-select
                    v-model="priority"
                    label="Priority"
                    :options="priority_options"
                />
            </apck-field>

            <apck-field :errors="errors?.notes">
                <apck-text
                    v-model="notes"
                    label="Notes"
                    :rows="10"
                />
            </apck-field>

            <apck-button title="Save" value="Save" icon="fa-save" class="btn-positive" :handler="save"/>
            <apck-mask :trigger="loading" :target="(mask) => mask.$parent.$parent.$refs.lightboxouter"/>
        </div>
    `
};

const ApckVulnWorkflowStatus = {
    components: {
        'apck-workflow-notes-table': ApckWorkflowNotesTables,
    },
    data: function () {
        return {
            status: null,
            status_options: [
                {'label': 'Unfixed', 'value': 'unfixed'},
                {'label': 'Fixed', 'value': 'fixed'},
                {'label': 'False Positive', 'value': 'false_positive'},
                {'label': 'Acceptable Risk', 'value': 'acceptable_risk'},
                {'label': 'Backported', 'value': 'backported'},
                {'label': 'Investigating', 'value': 'investigating'},
                {'label': 'Confirmed', 'value': 'confirmed'},
            ],
            suppression: null,
            suppression_options: [
                {'label': '14 Days', 'value': 14},
                {'label': '30 Days', 'value': 30},
                {'label': '60 Days', 'value': 60},
                {'label': '90 Days', 'value': 90},
                {'label': '6 Months', 'value': 180},
                {'label': '1 Year', 'value': 366},
                {'label': 'Forever', 'value': 99999},
            ],
            notes: '',
            loading: false,
            errorsKey: 0,
        };
    },
    props: {
        url: String,
        initial_status: String,
        initial_suppression: Number,
        notes_history: {
            type: Array,
            default: () => [],
        },
        errors: {
            type: Object,
            default: () => ({}),
        }
    },
    mounted: function () {
        const cmp = this;

        this.status = this.initial_status;
        this.suppression = this.initial_suppression;

        AjaxUtils.ajax_get({
            url: cmp.url,
            success: function (data) {
                if (data) {
                    cmp.$data.suppression = data.suppression_days;
                }
                cmp.loading = false;
            },
            fail: function () {
                cmp.loading = false;
            },
            opts: {err_404_message: 'Vulnerability not found'}
        })


    },
    methods: {
        save: function () {
            var cmp = this;
            cmp.loaded = false;

            cmp.$emit('updateErrors', {});
            cmp.errorsKey += 1;

            const data = {
                'status': cmp.status,
                'suppression': cmp.suppression,
                'notes': cmp.notes,
            };
            AjaxUtils.ajax_post({
                url: this.url,
                data: JSON.stringify(data),
                success: function (json, status, xhr) {
                    cmp.loaded = true;
                    cmp.$emit('statusUpdated', cmp.status);
                    cmp.$emit('close');
                },
                fail: function (json, status, xhr) {
                    cmp.$emit('updateErrors', json?.field_errors ? json.field_errors : {});
                    cmp.errorsKey += 1;
                    cmp.loaded = true;
                }
            });
        },
    },
    template: `
        <div ref="modal" :key="errorsKey">
            <div class="col col-xs-6">
                <apck-field :errors="errors?.status">
                    <apck-select
                        v-model="status"
                        label="Status"
                        :options="status_options"
                    />
                </apck-field>

                <apck-field :errors="errors?.suppression">
                    <apck-select
                        v-model="suppression"
                        label="Suppress vulnerability for"
                        :options="suppression_options"
                    />
                </apck-field>

                <apck-workflow-notes-table
                    :notes="notes_history"
                />

                <apck-field :errors="errors?.notes">
                    <apck-text
                        v-model="notes"
                        label="Notes"
                        :rows="10"
                    />
                </apck-field>

                <apck-button title="Save" value="Save" icon="fa-save" class="btn-positive" :handler="save"/>
                <apck-mask :trigger="loading" :target="(mask) => mask.$parent.$parent.$refs.lightboxouter"/>
            </div>
            <div class="col col-xs-6">
                <h4>De-Duplication and Vulnerability Suppression</h4>
                <p>
                When a vulnerability is received by AppCheck we attempt to de-duplicate it.
                </p>
                <p>
                Using a number of values (category, target, title, host, port, parameter, payload) we generate a unique signature for that vulnerability using a hash.
                </p>
                <p>
                If any of these values change then the vulnerability will be reported as new and will receive a new tracking ID because the hash will have changed.
                </p>
                <p>
                If the vulnerability is an <strong>INFO</strong> level vulnerability, then it is never de-duplicated as INFO findings contain useful information related to that particular run of the scan that can be helpful for debugging or looking back at point in time information.
                </p>
                <p>
                When a vulnerability is marked as fixed, regardless of suppression period if we detect it again it will be marked as unfixed,
                the reason for this, is that if you report an issue as fixed and it's detected again in a scan, it's considered that you would want
                to know this.
                </p>
                <p>
                If you want to apply a suppression period to the vulnerability and understand the risks, it needs to be marked as a <strong>"False Positive"</strong> or <strong>"Acceptable Risk"</strong>.
                </p>
                <p>
                If the vulnerability is then re-detected after the suppression period, <i>"Set back to unfixed due to detection after suppression expired"</i> will be added to the notes.
                </p>
            </div>
        </div>
    `
};

const ApckVulnWorkflowCategory = {
    data: function () {
        return {
            category: null,
            category_options: [
                {'label': 'Application', 'value': 'web_app'},
                {'label': 'Infrastructure', 'value': 'infra'},
            ],
            notes: '',
            loading: false,
            errorsKey: 0,
        };
    },
    props: {
        url: String,
        initial_category: String,
        errors: {
            type: Object,
            default: () => ({}),
        }
    },
    mounted: function () {
        this.category = this.initial_category;
    },
    methods: {
        save: function () {
            const cmp = this;
            cmp.loaded = false;

            cmp.$emit('updateErrors', {});
            cmp.errorsKey += 1;

            const data = {
                'category': cmp.category,
                'notes': cmp.notes,
            };
            AjaxUtils.ajax_post({
                url: this.url,
                data: JSON.stringify(data),
                success: function (category, status, xhr) {
                    cmp.loaded = true;
                    cmp.$emit('categoryUpdated', cmp.category);
                    cmp.$emit('close');
                },
                fail: function (json, status, xhr) {
                    cmp.$emit('updateErrors', json?.field_errors ? json.field_errors : {});
                    cmp.errorsKey += 1;
                    cmp.loaded = true;
                }
            });
        },
    },
    template: `
        <div ref="modal" :key="errorsKey">
            <apck-field :errors="errors?.category">
                <apck-select
                    v-model="category"
                    label="Category"
                    :options="category_options"
                />
            </apck-field>

            <apck-field :errors="errors?.notes">
                <apck-text
                    v-model="notes"
                    label="Notes"
                    :rows="10"
                />
            </apck-field>

            <apck-button title="Save" value="Save" icon="fa-save" class="btn-positive" :handler="save"/>
            <apck-mask :trigger="loading" :target="(mask) => mask.$parent.$parent.$refs.lightboxouter"/>
        </div>
    `
};

export const ApckVulnHistoryTable = {
    props: {
        url: {
            type: String,
            default: null
        },
        title: {
            type: String,
            default: 'Vulnerability Notes History'
        },
        actionBtnWidth: {
            type: Number,
            default: 30
        }
    },
    methods: {
        delete: function (row) {
            const cmp = this;
            const note_id = row.get('_id');
            cmp.$dialog.confirm(
                'Are you sure you want to delete this note?',
                'Delete vulnerability note',
                function () {
                    AjaxUtils.ajax_post({
                        url: `/vuln-control/notes/${note_id}/delete`,
                        success: function (data) {
                            cmp.$refs.history_table.store.reload();
                        },
                        fail: function () {
                            cmp.$refs.history_table.store.reload();
                        }
                    });
                }
            )
        },
    },
    data: function() {
        return {
            history_table_cols: [
                {"data": "created", "heading": "Date", "ctype": "datecolumn", "width": 150},
                {"data": "user", "heading": "Username", "ctype": "usercolumn", "canSearch": true, "width": 150},
                {"data": "action", "heading": "Action", "width": 180},
                {"data": "notes", "heading": "Notes", "ctype": "notescolumn", "minWidth": 220},
            ],
            actions: [ {
                title: "Delete",
                icon: "fa-trash",
                class: "btn-negative",
                handler: this.delete,
            }]
        };
    },
    template: `
        <apck-table
            ref="history_table"
            :title="title"
            :columns="history_table_cols"
            :actions="actions"
            :src="url"
            :actionBtnWidth="actionBtnWidth"
        />
    `
}

export const ApckVulnerability = {
    components: {
        'apck-scantable': ApckScanTable,
        'apck-vuln-history-table': ApckVulnHistoryTable,
        'apck-workflow-form': ApckWorkflowForm,
        'apck-vuln-attachments': ApckVulnAttachments,
    },
    props: {
        src: {
           type: String,
           default: '/vuln-control/details/'
        },
        _id: {
            type: String,
            default: null
        }
    },
    data: function() {
        return {
            vuln: {},
            integrations: {},
            loading: true,
            has_schedule: false,
            rescan_url: null,
            showFullAttackURL: false
        };
    },
    computed: {
        vulnerability_id: function () {
            if (this._id !== null) {
                return this._id;
            } else if (this.$route.params && this.$route.params.vulnerability_id) {
                return this.$route.params.vulnerability_id;
            }
            this.$flash({
                type: 'error',
                message: 'Could not find vulnerability'
            });
            this.$router.push({name: 'vulnerabilities.list'});
        },
        url: function() {
            return this.src + this.vulnerability_id;
        },
        notes_url: function() {
            return this.src + this.vulnerability_id + '/notes';
        },
        integrations_url: function() {
            return this.src + this.vulnerability_id +  '/integrations';
        },
        vuln_category_display: function () {
            if (this.vuln.category === 'infra') {
                return 'Infrastructure';
            } else if (this.vuln.category === 'web_app') {
                return 'Application';
            } else if (this.vuln.category === 'agent') {
                return 'Application';
            }
            return this.vuln.category;
        },
        vuln_priority_display: function () {
            return this.vuln.priority ? FormatImpactRisk(this.vuln.priority) : 'Unset';
        },
        impactStyle: function() {
            if (this.vuln.impact) {
                let rate = this.vuln.impact.substring(4);
                return `severity-${rate}-text`;
            }
        },
        priorityStyle: function() {
            if (this.vuln.priority) {
                let rate = this.vuln.priority.substring(4);
                return `severity-${rate}-text`;
            }
        },
        probabilityStyle: function() {
            if (this.vuln.probability) {
                let rate = this.vuln.probability.substring(4);
                return `severity-${rate}-text`;
            }
        },
        hasAttachments: function() {
            if (this.vuln.attachments && this.vuln.attachments.length > 0)
                return true;
            return false;
        },
        epssStyle: function() {
            const s = this.vuln.epss_base_score;
            if (s == null) return '';
            if (s >= 0.7) return 'severity-high-text';
            if (s >= 0.4) return 'severity-medium-text';
            return 'severity-low-text';
        },
        riskScoreStyle: function() {
            const s = this.vuln.risk_score;
            if (s == null) return '';
            if (s >= 80) return 'severity-crit-text';
            if (s >= 60) return 'severity-high-text';
            if (s >= 40) return 'severity-medium-text';
            return 'severity-low-text';
        },
        cvssDisplay: function() {
            const parts = [];
            if (this.vuln.cvss_base_score) parts.push('v2: ' + this.vuln.cvss_base_score);
            if (this.vuln.cvss_v3_base_score) parts.push('v3: ' + this.vuln.cvss_v3_base_score);
            if (this.vuln.cvss_v4_base_score) parts.push('v4: ' + this.vuln.cvss_v4_base_score);
            return parts.join(' / ') || '-';
        },
        scanDefinitionId: function () {
            // Get the scan definition ID to pass to attachments for building the scan settings URL
            if (this.vuln.scan_definition_id && this.vuln.scan_definition_id.length > 0) {
                return this.vuln.scan_definition_id[0]
            }
            return null
        }
    },
    methods: {
        toHTML: function (value) {
            return value; // todo: remove this once markup renderer works

            try {
                return ApckMarkupToHtml(value);
            } catch (e) {
                console.error(value);
                console.error(e);
                this.$flash({
                    type: 'error',
                    message: 'Error rendering markup'
                })
            }
        },
        sanitizeHTML(dirty) {
            let html = DOMPurify.sanitize(dirty, {
                ALLOWED_TAGS: [
                    'a',
                    'abbr',
                    'address',
                    'article',
                    'aside',
                    'b',
                    'blockquote',
                    'br',
                    'code',
                    'dd',
                    'details',
                    'div',
                    'dt',
                    'em',
                    'fieldset',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'hgroup',
                    'hr',
                    'i',
                    'legend',
                    'li',
                    'lo',
                    'ol',
                    'p',
                    'pre',
                    'section',
                    'small',
                    'span',
                    'strong',
                    'summary',
                    'table',
                    'tbody',
                    'td',
                    'th',
                    'thead',
                    'tr',
                    'ul',
                ],
                ALLOWED_ATTR: ['href', 'class'],
                KEEP_CONTENT: true,
                RETURN_DOM: true,
            });
            html.querySelectorAll('a').forEach((el) => {
                try {
                    let url = new URL(el.href);
                    if (!(url.host === 'appcheck-ng.com'
                        || url.host.endsWith('.appcheck-ng.com')
                        || url.host === 'localhost'
                        || url.host === '127.0.0.1')) {
                            el.setAttribute('rel', 'noopener noreferrer');
                            el.setAttribute('target', '_blank');
                            el.classList.add('external');
                    }
                } catch (_) {
                    // do nothing if url invalid
                }
            });
            return html.innerHTML;
        },
        addCollapsibleDetails(innerHtml) {
            const el = document.createElement('div');
            el.innerHTML = innerHtml;

            const collapsibles = el.querySelectorAll('.collapsible');
            collapsibles.forEach((collapsible) => {
                const legend = collapsible.querySelector('.vue-vuln-legend');
                const collapsed = collapsible.classList.contains('collapsed');
                if (legend) {
                    const legendInner = legend.innerHTML;
                    const icon = collapsed ? 'fa-caret-up' : 'fa-caret-down';
                    legend.innerHTML = `<span class="toggle"><i class="fa ${icon}"></i></span><span class="text">${legendInner}</span>`;

                    if (collapsed) {
                        collapsible.classList.add('expanded');
                    }
                }
            });
            return el.innerHTML;
        },
        handleDetailsCollapse($event) {
            if ($event.target.closest('.vue-vuln-legend')) {
                const collapsible = $event.target.closest('.collapsible');
                if (collapsible.classList.contains('collapsed')) {
                    collapsible.classList.remove('collapsed');
                    collapsible.classList.add('expanded');
                } else {
                    collapsible.classList.remove('expanded');
                    collapsible.classList.add('collapsed');
                }
                const fa = collapsible.querySelector('.fa');
                fa.classList.remove('fa-caret-up', 'fa-caret-down');
                fa.classList.add(collapsible.classList.contains('collapsed') ? 'fa-caret-up' : 'fa-caret-down');
            }
        },
        fetchSrc: function () {
            var cmp = this;
            this.loading = true;
            if (this.vulnerability_id) {
                var url = this.url;
                AjaxUtils.ajax_get({
                    url: url,
                    success: function (data) {
                        if (data) {
                            cmp.$data.vuln = $.extend({}, data.vulnerability);
                            cmp.$data.has_schedule = data.has_schedule;
                            cmp.$data.rescan_url = data.rescan_url;
                            // set workflow form data, after workflowForm $ref is initialised
                            cmp.$nextTick(() => {
                                Object.assign(cmp.$refs.workflowForm.$data, data.workflow_form.initial);
                                cmp.$refs.workflowForm.$data.assignee_options = data.workflow_form.assignee_options;
                                cmp.$refs.workflowForm.$data.notes_history = data.workflow_form.notes_history;
                            });
                        }
                        cmp.loading = false;
                    },
                    fail: function () {
                        cmp.loading = false;
                    },
                    opts: {err_404_message: 'Vulnerability not found'}
                });
            }
        },
        editStatus() {
            const cmp = this;
            var errors = {};
            const lightbox_conf = {
                title: 'Edit Status',
                component: ApckVulnWorkflowStatus,
                bind: {
                    url: `/vuln-control/details/${cmp.vulnerability_id}/status`,
                    initial_status: cmp.vuln.status ?? null,
                    initial_suppression: cmp.vuln.suppression_days ?? null,
                    notes_history: cmp.$refs.workflowForm.$data.notes_history,
                    errors: errors
                },
                on: {
                    statusUpdated: (status) => {
                        cmp.vuln.status = status;
                        cmp.workflowFormUpdated();
                    },
                    updateErrors: (err) => {
                        // clear old keys
                        Object.keys(errors).forEach(key => {
                            Vue.delete(errors, key);
                        });
                        // set new keys
                        Object.assign(errors, err);
                    }
                },
                size: "normal",
            };
            cmp.$lightbox.open(lightbox_conf);
        },
        editPriority() {
            const cmp = this;
            var errors = {};
            const lightbox_conf = {
                title: 'Edit Priority',
                component: ApckVulnWorkflowPriority,
                bind: {
                    url: `/vuln-control/details/${cmp.vulnerability_id}/priority`,
                    initial_priority: cmp.vuln.priority ?? null,
                    errors: errors
                },
                on: {
                    priorityUpdated: (priority) => {
                        cmp.vuln.priority = priority;
                        cmp.workflowFormUpdated();
                    },
                    updateErrors: (err) => {
                        // clear old keys
                        Object.keys(errors).forEach(key => {
                            Vue.delete(errors, key);
                        });
                        // set new keys
                        Object.assign(errors, err);
                    }
                },
                size: "narrow",
            };
            cmp.$lightbox.open(lightbox_conf);
        },
        editAssignee() {
            const cmp = this;
            var errors = {};
            const lightbox_conf = {
                title: 'Edit Assignee',
                component: ApckVulnWorkflowAssignee,
                bind: {
                    url: `/vuln-control/details/${cmp.vulnerability_id}/assign`,
                    initial_assignee: cmp.vuln.assignee_summary?._id ?? null,
                    errors: errors
                },
                on: {
                    assigneeUpdated: (assignee_summary) => {
                        cmp.vuln.assignee_summary = assignee_summary;
                        cmp.workflowFormUpdated();
                    },
                    updateErrors: (err) => {
                        // clear old keys
                        Object.keys(errors).forEach(key => {
                            Vue.delete(errors, key);
                        });
                        // set new keys
                        Object.assign(errors, err);
                    }
                },
                size: "narrow",
            };
            cmp.$lightbox.open(lightbox_conf);
        },
        editCategory() {
            const cmp = this;
            var errors = {};
            const lightbox_conf = {
                title: 'Edit Category',
                component: ApckVulnWorkflowCategory,
                bind: {
                    url: `/vuln-control/details/${cmp.vulnerability_id}/category`,
                    initial_category: cmp.vuln.category ?? null,
                    errors: errors
                },
                on: {
                    categoryUpdated: (category) => {
                        cmp.vuln.category = category;
                        cmp.workflowFormUpdated();
                    },
                    updateErrors: (err) => {
                        // clear old keys
                        Object.keys(errors).forEach(key => {
                            Vue.delete(errors, key);
                        });
                        // set new keys
                        Object.assign(errors, err);
                    }
                },
                size: "narrow",
            };
            cmp.$lightbox.open(lightbox_conf);
        },
        workflowFormUpdated() {
            this.$emit('workflow-updated');
            this.fetchSrc();
        },
        handleLinkClick(event) {
            const link = event.target.closest('a.external');
                if (link) {
                    event.preventDefault();
                    this.showExternalLinkWarning(link.href);
            }
        },
        showExternalLinkWarning(url) {
            this.$dialog.confirm(
                `We have provided a reference to this site because it has information that may be of interest in relation to this vulnerability. You are leaving our site to visit an external link (opens in a new tab). Continue?`,
                'External Navigation',
                () => {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            );
        },
    },
    mounted: function () {
        this.fetchSrc();
    },
    template: `
        <div v-if="vuln.title" class="vuln-details">
            <div class="title-bar">
                <h1 class="title">
                    {{ vuln.title }}

                    <router-link
                        v-if="globalFlags.spa"
                        :to="{name: 'vulnerabilities.view', params: {vulnerability_id: vulnerability_id}}"
                        @click.native="$emit('close')"
                        title="Link to this vulnerability"
                        alt="Link to this vulnerability"
                    >
                        <i class="fa fa-external-link" style="margin-left: 5px;"></i>
                    </router-link>
                    <a
                        v-else
                        :href="'/vulnerabilities/' + vulnerability_id"
                        title="Link to this vulnerability"
                    >
                        <i class="fa fa-external-link" style="margin-left: 5px;"></i>
                    </a>
                </h1>

                <form v-if="rescan_url" :action="rescan_url">
                    <button class="btn-primary btn" value="rescan">Rescan</button>
                </form>
            </div>

            <div v-if="vuln.actively_exploited || vuln.internal" class="vuln-detail-badges">
                <span v-if="vuln.actively_exploited" class="badge badge-danger" title="Known to be actively exploited">
                    <i class="fa fa-warning"></i> Actively Exploited
                </span>
                <span v-if="vuln.internal" class="badge badge-info" title="Internal asset">
                    <i class="fa fa-lock"></i> Internal
                </span>
            </div>

            <div class="vuln-detail-cards">
                <div class="vuln-detail-card">
                    <h4>Status</h4>
                    <span>{{ vuln.status | formatStatus }}</span>
                    <apck-button
                        value="Edit"
                        class="btn-link"
                        :handler="editStatus"
                    />
                </div>

                <div class="vuln-detail-card">
                    <h4>Priority</h4>
                    <span :class="[priorityStyle, 'severity']">{{ vuln_priority_display }}</span>
                    <apck-button
                        value="Edit"
                        class="btn-link"
                        :handler="editPriority"
                    />
                </div>

                <div class="vuln-detail-card">
                    <h4>Impact</h4>
                    <span :class="[impactStyle, 'severity']">{{ vuln.impact | formatRisk }}</span>
                    <p :title="cvssDisplay">
                        CVSS:<b> {{ cvssDisplay }}</b>
                    </p>
                </div>

                <div class="vuln-detail-card">
                    <h4>Probability</h4>
                    <span :class="[probabilityStyle, 'severity']">{{ vuln.probability | formatRisk }}</span>
                </div>

                <div class="vuln-detail-card">
                    <h4>Assigned to</h4>
                    <span>{{ vuln.assignee_summary?.name || 'Unassigned' }}</span>
                    <apck-button
                        value="Edit"
                        class="btn-link"
                        :handler="editAssignee"
                    />
                </div>

                <div class="vuln-detail-card">
                    <h4>First Detected</h4>
                    <template v-if="vuln.first_detected_at">
                        <span>{{ vuln.first_detected_at * 1000 | formatDateFromNow }}</span>
                        <p>{{ vuln.first_detected_at * 1000 | formatTimestamp }}</p>
                    </template>
                    <span v-else>-</span>
                </div>

                <div class="vuln-detail-card">
                    <h4>Last Detected</h4>
                    <template v-if="vuln.first_detected_at">
                        <span>{{ vuln.last_detected_at * 1000 | formatDateFromNow }}</span>
                        <p>{{ vuln.last_detected_at * 1000 | formatTimestamp }}</p>
                    </template>
                    <span v-else>-</span>
                </div>

                <div class="vuln-detail-card" v-if="vuln.epss_base_score != null">
                    <h4>EPSS</h4>
                    <span :class="[epssStyle, 'severity']">{{ (vuln.epss_base_score * 100).toFixed(1) }}%</span>
                    <p>Exploitation probability</p>
                </div>

                <div class="vuln-detail-card" v-if="vuln.risk_score != null">
                    <h4>Risk Score</h4>
                    <span :class="[riskScoreStyle, 'severity']">{{ vuln.risk_score.toFixed(0) }}</span>
                    <p>/ 100</p>
                </div>

                <div class="vuln-detail-card" v-if="vuln.sla_due_date">
                    <h4>SLA Due</h4>
                    <span :class="vuln.sla_breached ? 'severity-high-text' : ''">{{ vuln.sla_due_date * 1000 | formatDateFromNow }}</span>
                    <p v-if="vuln.sla_breached" class="severity-high-text"><i class="fa fa-exclamation-triangle"></i> Breached</p>
                    <p v-else>{{ vuln.sla_due_date * 1000 | formatTimestamp }}</p>
                </div>
            </div>

            <!-- disable vuln layers for now
            <div class="vuln-detail-layers">
                <h3>Vulnerability Layer</h3>
                <div class="vuln-layers">
                    <ul class="layers">
                        <li title="OSINT">
                            <a href="javascript:;">
                                1: OSINT
                            </a>
                        </li>
                        <li title="Gateway">
                            <a href="javascript:;">
                                2: Gateway
                            </a>
                        </li>
                        <li title="Network Delivery">
                            <a href="javascript:;">
                                3: Network Delivery
                            </a>
                        </li>
                        <li title="Application Framework" class="exploit">
                            <a href="javascript:;">
                                4: Application Framework
                            </a>
                        </li>
                        <li title="Application Code (DAST)">
                            <a href="javascript:;">
                                5: Application Code (DAST)
                            </a>
                        </li>
                        <li title="Cloud / Third Party Trust">
                            <a href="javascript:;">
                                6: Cloud / Third Party Trust
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            -->

            <div class="vuln-detail-tables">
                <table>
                    <tr>
                        <th>Vulnerability Category</th>
                        <td>
                            {{ vuln_category_display }}
                            <apck-button
                                title="Edit"
                                icon="fa-pencil"
                                class="btn-link"
                                :handler="editCategory"
                            />
                        </td>
                    </tr>
                    <template v-if="vuln.category === 'infra'">
                        <tr>
                            <th>Host</th>
                            <td>{{ vuln.host }}</td>
                        </tr>
                        <tr>
                            <th>Port</th>
                            <td>{{ vuln.port }}</td>
                        </tr>
                    </template>
                    <tr v-else>
                        <th>Application</th>
                        <td>{{ vuln.host }}:{{ vuln.port }}</td>
                    </tr>
                    <tr v-if="vuln.parameter">
                        <th>Vulnerable Parameter</th>
                        <td>{{ vuln.parameter }}</td>
                    </tr>
                    <tr v-if="vuln.cves && vuln.cves.length">
                        <th>CVEs</th>
                        <td>
                            <span v-for="(cve, idx) in vuln.cves" :key="cve">
                                <a :href="'https://nvd.nist.gov/vuln/detail/' + cve" target="_blank" rel="noopener noreferrer">{{ cve }}</a><span v-if="idx < vuln.cves.length - 1">, </span>
                            </span>
                        </td>
                    </tr>
                    <tr v-if="vuln.tags && vuln.tags.length">
                        <th>Tags</th>
                        <td>
                            <span v-for="tag in vuln.tags" :key="tag" class="vuln-tag">{{ tag }}</span>
                        </td>
                    </tr>
                    <tr v-if="vuln.patch && vuln.patch.length">
                        <th>Patches</th>
                        <td>
                            <span v-for="(p, idx) in vuln.patch" :key="idx">{{ p }}<span v-if="idx < vuln.patch.length - 1">, </span></span>
                        </td>
                    </tr>
                </table>

                <table>
                    <tr v-if="vuln.target">
                        <th>Target</th>
                        <td>{{ vuln.target }}</td>
                    </tr>
                    <tr v-if="vuln.attack">
                        <th>Attack URL</th>
                        <td class="monospace">
                            {{ vuln.attack.length > 70 && !showFullAttackURL ? vuln.attack.substring(0, 20) + '...' : vuln.attack}}
                            <span
                                class="btn-link btn"
                                tabindex="0"
                                v-if="vuln.attack.length > 70"
                                @click="showFullAttackURL = !showFullAttackURL"
                                @keyup.space="showFullAttackURL = !showFullAttackURL"
                            >
                                Show {{ showFullAttackURL ? 'less' : 'more' }}
                            </span>
                        </td>
                    </tr>
                </table>
            </div>

            <div class="vuln-detail-tabs" @click="handleLinkClick">
                <apck-tabs>
                    <apck-tab title="Description" :active="true">
                        <div v-if="vuln.rendered_description">
                            <h3>Description</h3>
                            <div class="mermaid" style="display: none;">
                                sequenceDiagram
                                Firewall->>Application Gateway: HTTP Request
                                Application Gateway->>Application Server: Forward Request
                                Application Server-->>Application Gateway: Response
                                Application Gateway-->>Firewall: Final Response
                            </div>
                            <div v-html="sanitizeHTML(vuln.rendered_description)" class="c-vuln-description-module">
                            </div>
                        </div>
                        <br/>
                        <div v-if="vuln.rendered_solution">
                            <h3>Solution</h3>
                            <div v-html="sanitizeHTML(vuln.rendered_solution)" class="c-vuln-description-module">
                            </div>
                        </div>
                    </apck-tab>
                    <apck-tab title="Details" v-if="vuln.rendered_details">
                        <div
                            v-html="addCollapsibleDetails(sanitizeHTML(vuln.rendered_details))"
                            class="c-vuln-details-module"
                            @click="handleDetailsCollapse($event)"
                        ></div>
                        <!--
                        <div v-for="(content, title) in vuln.details" :key="title">
                            <h2>{{ title }}</h2>
                            <div v-html="toHTML(content)">
                            </div>
                        </div>
                        -->
                    </apck-tab>
                    <apck-tab title="Workflow">
                        <apck-workflow-form
                            ref="workflowForm"
                            :url="url"
                            @workflow-updated="workflowFormUpdated()"
                        />
                    </apck-tab>
                    <apck-tab v-if="hasAttachments" title="Attachments">
                        <apck-vuln-attachments
                            :attachments="vuln.attachments"
                            :scan-definition-id="scanDefinitionId"
                        />
                    </apck-tab>
                    <apck-tab title="Linked Scans">
                        <apck-scantable
                            :src="'/scans/fromvuln/'+vuln._id"
                        />
                    </apck-tab>
                    <apck-tab title="History">
                        <apck-vuln-history-table
                            ref="history_table"
                            :url="notes_url"
                        />
                    </apck-tab>
                    <apck-tab title="Integrations">
                        <div v-if="integration.jira">
                            <h3>Jira</h3>
                            <hr />
                            <ul>
                                <li>Link ID: {{ integration.jira.link.id }}</li>
                                <li>Issue ID: {{ integration.jira.link.issue_id }}</li>
                                <li>Comment ID: {{ integration.jira.link.comment_id }}</li>
                                <li>Created At: {{ integration.jira.link.created_at }}</li>
                                <li>Updated At: {{ integration.jira.link.updated_at }}</li>
                                <li>Profile ID: {{ integration.jira.profile.id }}</li>
                                <li>Profile Name: {{ integration.jira.profile.description }}</li>
                            </ul>
                        </div>
                    </apck-tab>
                </apck-tabs>
            </div>
        </div>

        <h1 v-else-if="!loading">
            Failure to load vulnerability data
        </h1>
    `
}
